import { z } from "zod";

/** Frozen copy of version-1 word types. Do not reuse the moving domain constant here. */
export const V1_WORD_TYPES = [
  "noun",
  "verb",
  "phrasal_verb",
  "adjective",
  "adverb",
  "phrase",
  "idiom",
  "other",
] as const;

export const vocabularySnapshotV1WordTypeSchema = z.enum(V1_WORD_TYPES);

export const vocabularySnapshotV1WordEntrySchema = z.object({
  id: z.string().min(1),
  normalizedText: z.string().min(1),
  displayText: z.string().min(1),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
});

const v1RelatedDistinctionSchema = z.object({
  meaningId: z.string().min(1),
  explanation: z.string(),
});

const v1ExerciseBase = {
  id: z.string().min(1),
  question: z.string().min(1),
  acceptedAnswers: z.array(z.string().min(1)).min(1),
  correctAnswer: z.string().min(1),
  explanation: z.string(),
  referencedMeaningIds: z.array(z.string()),
  relatedWordDistinctions: z.array(v1RelatedDistinctionSchema),
};

export const vocabularySnapshotV1ExerciseSchema = z.discriminatedUnion("kind", [
  z.object({ ...v1ExerciseBase, kind: z.literal("context_recall") }),
  z.object({ ...v1ExerciseBase, kind: z.literal("meaning_to_word") }),
  z.object({
    ...v1ExerciseBase,
    kind: z.literal("confusable_context"),
    options: z.array(z.object({ id: z.string().min(1), label: z.string().min(1) })).min(2).max(4),
    correctOptionId: z.string().min(1),
  }),
]);

export const vocabularySnapshotV1MeaningCardSchema = z.object({
  id: z.string().min(1),
  wordId: z.string().min(1),
  type: vocabularySnapshotV1WordTypeSchema,
  shortLabel: z.string().min(1),
  definition: z.string().min(1),
  russianHint: z.string().nullable(),
  example: z.string().min(1),
  memoryKey: z.string().nullable(),
  collocations: z.array(z.string()),
  contentRevision: z.number().int().nonnegative(),
  practiceReadiness: z.enum(["ready", "needs_regeneration"]),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
});

export const vocabularySnapshotV1MeaningRelationSchema = z.object({
  sourceMeaningId: z.string().min(1),
  targetMeaningId: z.string().min(1),
  explanation: z.string().min(1),
  revision: z.number().int().nonnegative(),
});

export const vocabularySnapshotV1ExerciseSetSchema = z.object({
  meaningId: z.string().min(1),
  sourceContentRevision: z.number().int().nonnegative(),
  dependencyRevisions: z.record(z.string(), z.number().int().nonnegative()),
  generationMetadata: z.object({
    model: z.string(),
    generatedAt: z.string(),
    promptVersion: z.string(),
  }),
  exercises: z.array(vocabularySnapshotV1ExerciseSchema),
  status: z.enum(["ready", "invalidated"]),
});

export const vocabularySnapshotV1PracticeStateSchema = z.object({
  meaningId: z.string().min(1),
  practiced: z.boolean(),
  lastExerciseId: z.string().nullable(),
});

export const vocabularySnapshotV1Schema = z
  .object({
    schemaVersion: z.literal(1),
    dataRevision: z.number().int().nonnegative(),
    cycleId: z.number().int().positive(),
    words: z.array(vocabularySnapshotV1WordEntrySchema),
    meanings: z.array(vocabularySnapshotV1MeaningCardSchema),
    relations: z.array(vocabularySnapshotV1MeaningRelationSchema),
    exerciseSets: z.array(vocabularySnapshotV1ExerciseSetSchema),
    practiceStates: z.array(vocabularySnapshotV1PracticeStateSchema),
  })
  .strict();

export type VocabularySnapshotV1 = z.infer<typeof vocabularySnapshotV1Schema>;
